import { Link } from "react-router-dom";
import { ArrowLeft, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 py-6">
        <div className="max-w-4xl mx-auto px-6 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 rounded-sm gap-1">
              <ArrowLeft className="h-4 w-4" /> Torna al sito
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 bg-[#c8e414] rounded-full flex items-center justify-center">
              <Trophy className="h-3.5 w-3.5 text-slate-900" strokeWidth={2.5} />
            </div>
            <span className="font-heading text-lg font-bold text-white uppercase tracking-tight">Tabellone Facile</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mb-2">
          Informativa sulla Privacy
        </h1>
        <p className="text-sm text-slate-500 mb-10">Ultimo aggiornamento: Aprile 2026</p>

        <div className="prose prose-slate max-w-none space-y-8 text-sm leading-relaxed text-slate-700">

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">1. Titolare del trattamento</h2>
            <p>
              Il titolare del trattamento dei dati personali raccolti tramite la piattaforma <strong>Tabellone Facile</strong> (di seguito "Piattaforma" o "Servizio") 
              e il soggetto giuridico che gestisce il servizio, raggiungibile tramite il sito web e l'applicazione web.
            </p>
            <p>Per qualsiasi richiesta relativa al trattamento dei dati personali, e possibile scrivere a: <strong>privacy@tabellonefacile.net</strong></p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">2. Normativa di riferimento</h2>
            <p>
              La presente informativa e resa ai sensi del <strong>Regolamento (UE) 2016/679</strong> (GDPR - General Data Protection Regulation) 
              e del <strong>D.Lgs. 30 giugno 2003, n. 196</strong> (Codice in materia di protezione dei dati personali), come modificato dal 
              D.Lgs. 10 agosto 2018, n. 101.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">3. Dati personali raccolti</h2>
            <p>La Piattaforma raccoglie e tratta le seguenti categorie di dati personali:</p>

            <div className="bg-white border border-slate-200 rounded-sm p-4 mt-3 space-y-3">
              <div>
                <h3 className="font-bold text-slate-900 text-sm">a) Dati di registrazione e autenticazione</h3>
                <ul className="list-disc list-inside mt-1 space-y-1 text-slate-600">
                  <li>Nome e cognome</li>
                  <li>Indirizzo email</li>
                  <li>Numero di telefono</li>
                  <li>Password (in forma criptata)</li>
                  <li>Dati di autenticazione tramite provider terzi (Google, Facebook)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-sm">b) Dati di fatturazione e pagamento</h3>
                <ul className="list-disc list-inside mt-1 space-y-1 text-slate-600">
                  <li>Ragione sociale / Partita IVA</li>
                  <li>Codice SDI e PEC</li>
                  <li>Dati di pagamento tramite Stripe (carte di credito/debito)</li>
                  <li>Dati di pagamento tramite PayPal (email PayPal, ID transazione)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-sm">c) Dati relativi ai tornei</h3>
                <ul className="list-disc list-inside mt-1 space-y-1 text-slate-600">
                  <li>Nomi, email e numeri di telefono dei giocatori registrati</li>
                  <li>Preferenze di orario dei giocatori</li>
                  <li>Risultati delle partite e classifiche</li>
                  <li>Informazioni sul circolo (nome, indirizzo, logo)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-sm">d) Dati tecnici e di navigazione</h3>
                <ul className="list-disc list-inside mt-1 space-y-1 text-slate-600">
                  <li>Indirizzo IP</li>
                  <li>Tipo di browser e dispositivo</li>
                  <li>Cookie tecnici e di sessione</li>
                  <li>Log di accesso e interazione con il servizio</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">4. Finalita e base giuridica del trattamento</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-200 rounded-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left p-3 font-bold text-slate-900 border-b">Finalita</th>
                    <th className="text-left p-3 font-bold text-slate-900 border-b">Base giuridica (Art. GDPR)</th>
                  </tr>
                </thead>
                <tbody className="text-slate-600">
                  <tr className="border-b"><td className="p-3">Creazione e gestione dell'account utente</td><td className="p-3">Art. 6(1)(b) - Esecuzione contratto</td></tr>
                  <tr className="border-b"><td className="p-3">Erogazione del servizio di gestione tornei</td><td className="p-3">Art. 6(1)(b) - Esecuzione contratto</td></tr>
                  <tr className="border-b"><td className="p-3">Gestione degli abbonamenti e dei pagamenti</td><td className="p-3">Art. 6(1)(b) - Esecuzione contratto</td></tr>
                  <tr className="border-b"><td className="p-3">Invio di notifiche email relative alle partite</td><td className="p-3">Art. 6(1)(b) - Esecuzione contratto</td></tr>
                  <tr className="border-b"><td className="p-3">Invio di promemoria scadenza abbonamento</td><td className="p-3">Art. 6(1)(f) - Legittimo interesse</td></tr>
                  <tr><td className="p-3">Adempimenti fiscali e contabili</td><td className="p-3">Art. 6(1)(c) - Obbligo legale</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">5. Destinatari dei dati</h2>
            <p>I dati personali potranno essere comunicati a:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>Stripe, Inc.</strong> e <strong>PayPal (Europe) S.a r.l.</strong> - per l'elaborazione dei pagamenti</li>
              <li><strong>Twilio SendGrid</strong> - per l'invio di email transazionali e notifiche</li>
              <li><strong>Google LLC</strong> e <strong>Meta Platforms, Inc.</strong> - per l'autenticazione tramite social login</li>
              <li><strong>MongoDB, Inc.</strong> - per l'hosting del database</li>
              <li>Autorita competenti, ove richiesto dalla legge</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">6. Trasferimento dati extra-UE</h2>
            <p>
              Alcuni dei fornitori di servizi sopra indicati hanno sede negli Stati Uniti d'America. Il trasferimento dei dati verso tali soggetti 
              avviene sulla base delle <strong>Clausole Contrattuali Standard</strong> approvate dalla Commissione Europea (Art. 46(2)(c) GDPR) 
              e/o sulla base del <strong>EU-U.S. Data Privacy Framework</strong>, ove applicabile.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">7. Periodo di conservazione</h2>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Dati dell'account:</strong> conservati per tutta la durata dell'account e per 30 giorni dalla cancellazione</li>
              <li><strong>Dati dei tornei:</strong> conservati per tutta la durata dell'abbonamento attivo</li>
              <li><strong>Dati di fatturazione:</strong> conservati per 10 anni come previsto dalla normativa fiscale italiana</li>
              <li><strong>Log tecnici:</strong> conservati per un massimo di 12 mesi</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">8. Diritti dell'interessato</h2>
            <p>Ai sensi degli artt. 15-22 del GDPR, l'interessato ha il diritto di:</p>
            <div className="grid sm:grid-cols-2 gap-2 mt-3">
              {[
                "Accesso ai propri dati personali",
                "Rettifica dei dati inesatti",
                "Cancellazione dei dati (diritto all'oblio)",
                "Limitazione del trattamento",
                "Portabilita dei dati",
                "Opposizione al trattamento",
                "Revoca del consenso in qualsiasi momento",
                "Proporre reclamo al Garante per la protezione dei dati personali"
              ].map((right, i) => (
                <div key={right} className="flex items-start gap-2 bg-white border border-slate-200 rounded-sm p-3">
                  <span className="text-green-700 font-bold text-xs mt-0.5">{i + 1}.</span>
                  <span className="text-sm">{right}</span>
                </div>
              ))}
            </div>
            <p className="mt-3">
              Per esercitare i propri diritti, e possibile scrivere a <strong>privacy@tabellonefacile.net</strong>.
            </p>
            <p className="mt-2">
              E inoltre possibile proporre reclamo al <strong>Garante per la protezione dei dati personali</strong> 
              (www.garanteprivacy.it).
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">9. Sicurezza dei dati</h2>
            <p>
              La Piattaforma adotta misure tecniche e organizzative adeguate per proteggere i dati personali, tra cui:
              crittografia delle password (bcrypt), comunicazioni HTTPS/TLS, accesso limitato ai dati su base role-based, 
              e monitoraggio continuo dei sistemi.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">10. Modifiche alla presente informativa</h2>
            <p>
              Il Titolare si riserva il diritto di modificare la presente informativa in qualsiasi momento. 
              Le modifiche saranno pubblicate su questa pagina con indicazione della data di ultimo aggiornamento. 
              L'uso continuato del Servizio dopo la pubblicazione delle modifiche costituisce accettazione delle stesse.
            </p>
          </section>
        </div>
      </main>

      <footer className="bg-slate-900 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-xs">Tabellone Facile - Gestione intelligente dei tornei di tennis</p>
        </div>
      </footer>
    </div>
  );
}
